import LoyaltyOffer from "../LoyaltyOfferClass/LoyaltyOffer";
import { createLoyaltyOffer } from "../LoyaltyOfferClass/LoyaltyOfferCreator/loyaltyOfferCreator";
import {
  LoyaltyCustomerInfo,
  LoyaltyTierSystem,
  LoyaltyTierSystemTier,
} from "../loyaltyTypes";
import {
  getAvailableMilestoneOfferIds,
  getPointsToUnlock,
  sortMilestonesByPoints,
} from "./milestoneQueries";

export type LoyaltyCardMilestoneViewModel = {
  milestoneId: string;
  title: string;
  titleLocalized?: string;
  points: number;
  pointsToUnlock: number;
  isUnlocked: boolean;
  /**
   * The member is looking at a tier BELOW their own, so this milestone's
   * threshold is already behind them and "X more points to unlock" is
   * meaningless (prepits/project#4107, point 6). Distinct from `isUnlocked`:
   * loyalty-service still only makes their OWN tier's offers redeemable, so
   * the cards stay locked - it is the threshold copy that has to go.
   */
  tierAlreadyPassed: boolean;
  offerInstances: LoyaltyOffer[];
};

type LoyaltyCardViewModel = {
  pointsBalance: number;
  pointName: string;
  currentTier?: LoyaltyTierSystemTier;
  milestones: LoyaltyCardMilestoneViewModel[];
};

export const buildLoyaltyCardViewModel = ({
  customerInfo,
  tierSystem,
  pointName,
  locale = "en",
  tierId,
}: {
  customerInfo?: LoyaltyCustomerInfo | null;
  tierSystem?: LoyaltyTierSystem | null;
  pointName: string;
  locale?: string;
  /**
   * Show a specific tier's milestones instead of the member's own - used when
   * arriving from a tier's "View all rewards" on the tiers overview page.
   */
  tierId?: string;
}): LoyaltyCardViewModel => {
  const pointsBalance = customerInfo?.availablePoints ?? 0;
  const requestedTier = tierId
    ? tierSystem?.tiers.find((tier) => tier.tierId === tierId)
    : undefined;
  const customerTierId =
    customerInfo?.tierProgress?.currentTierId ?? tierSystem?.tiers[0]?.tierId;

  const currentTier =
    requestedTier ??
    tierSystem?.tiers.find((tier) => tier.tierId === customerTierId);
  const isOtherTier = Boolean(
    requestedTier && requestedTier.tierId !== customerTierId
  );
  const customerTier = tierSystem?.tiers.find(
    (tier) => tier.tierId === customerTierId
  );
  // `tierOrder` is what sortLoyaltyTiers ranks by, so a lower one is a tier the
  // member has already come up through.
  const tierAlreadyPassed = Boolean(
    requestedTier &&
      customerTier &&
      requestedTier.tierOrder < customerTier.tierOrder
  );
  const availableOfferIdsByMilestone =
    getAvailableMilestoneOfferIds(customerInfo);

  const milestones = sortMilestonesByPoints(currentTier?.milestones ?? []).map(
    (milestone) => {
      const baseLocale = locale.split("-")[0];
      const title =
        milestone.name[locale] ??
        milestone.name[baseLocale] ??
        milestone.name.en ??
        Object.values(milestone.name).find(Boolean) ??
        "";
      const titleLocalized =
        locale === "en"
          ? undefined
          : milestone.name[locale] ?? milestone.name[baseLocale];
      // loyalty-service only makes the member's own tier's milestone offers
      // redeemable, so another tier's rewards stay locked however many points
      // they hold.
      const pointsToUnlock = isOtherTier
        ? milestone.points
        : getPointsToUnlock({
            milestonePoints: milestone.points,
            availablePoints: pointsBalance,
          });
      const allowedOfferIds = availableOfferIdsByMilestone.get(
        milestone.milestoneId
      );
      const rawOffers =
        allowedOfferIds && allowedOfferIds.size > 0
          ? milestone.offers.filter((offer) => allowedOfferIds.has(offer.id))
          : milestone.offers;

      return {
        milestoneId: milestone.milestoneId,
        title,
        titleLocalized,
        points: milestone.points,
        pointsToUnlock,
        isUnlocked: pointsToUnlock === 0,
        tierAlreadyPassed,
        offerInstances: rawOffers.map((offer) =>
          createLoyaltyOffer({
            ...offer,
            milestoneId: milestone.milestoneId,
            tierId: customerTierId,
            milestonePoints: milestone.points,
          })
        ),
      };
    }
  );

  return {
    pointsBalance,
    pointName,
    currentTier,
    milestones,
  };
};
